import { useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { useAuthStore } from '../stores/authStore';

const AUTH_STORAGE_KEY = 'auth-storage';

function decodeJwtExp(accessToken: string | null): number | null {
  if (!accessToken) return null;
  try {
    const part = accessToken.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * When another tab logs in/out or refreshes tokens, sync this tab from localStorage
 * so refresh tokens stay valid (avoids "random" logout on multi-tab use).
 */
export function useAuthCrossTabSync() {
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_STORAGE_KEY || e.storageArea !== localStorage) return;
      try {
        if (!e.newValue) {
          useAuthStore.getState().clearAuth();
          return;
        }
        const parsed = JSON.parse(e.newValue) as { state?: Record<string, unknown> };
        const state = parsed.state;
        if (!state) return;
        const refreshToken = state.refreshToken as string | null | undefined;
        const accessToken = state.accessToken as string | null | undefined;
        const user = state.user as Record<string, unknown> | null | undefined;
        if (refreshToken && accessToken && user) {
          type AuthUser = NonNullable<ReturnType<typeof useAuthStore.getState>['user']>;
          useAuthStore.setState({
            refreshToken,
            accessToken,
            user: user as unknown as AuthUser,
            isAuthenticated: true,
            isLoading: false,
          });
        }
      } catch {
        /* ignore corrupt storage */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
}

/**
 * Refresh access token shortly before expiry so long idle periods don't always hit 401.
 */
export function useProactiveTokenRefresh() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const exp = decodeJwtExp(accessToken);
    if (!exp) return;

    const msUntilRefresh = exp * 1000 - Date.now() - 90_000;

    const runRefresh = async () => {
      const { refreshToken, setAuth } = useAuthStore.getState();
      if (!refreshToken) return;
      try {
        const response = await authService.refresh(refreshToken);
        const meData = await authService.getMe(response.accessToken);
        setAuth(meData, response.accessToken, response.refreshToken);
      } catch {
        /* next API call may trigger interceptor refresh */
      }
    };

    if (msUntilRefresh <= 0) {
      void runRefresh();
      return;
    }

    timerRef.current = window.setTimeout(() => void runRefresh(), msUntilRefresh);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [accessToken]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const exp = decodeJwtExp(useAuthStore.getState().accessToken);
      if (!exp) return;
      if (exp * 1000 - Date.now() > 120_000) return;

      const { refreshToken, setAuth } = useAuthStore.getState();
      if (!refreshToken) return;
      void (async () => {
        try {
          const response = await authService.refresh(refreshToken);
          const meData = await authService.getMe(response.accessToken);
          setAuth(meData, response.accessToken, response.refreshToken);
        } catch {
          /* ignore */
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
}
